import { z } from 'zod'
import { isFieldVisible } from '../fieldVisibility'
import { JsonFormPropertyV2, RequireWhenAnyOf, RequireWhenOperator, RequireWhenRule } from '../types'
import { evalOp, evalGroup } from './conditionEvaluation'

const isRecord = (o: unknown): o is Record<string, unknown> => typeof o === 'object' && o !== null

const getEnumValue = (o: unknown): string | number | undefined => {
  if (!isRecord(o)) return undefined
  const v = o.value as unknown
  return typeof v === 'string' || typeof v === 'number' ? v : undefined
}

const buildNumberLiteralUnion = (values: number[]): z.ZodType<number> => {
  const [first, ...rest] = values
  let schema: z.ZodType<number> = z.literal(first)
  for (const n of rest) {
    schema = schema.or(z.literal(n))
  }
  return schema
}

export const generateZodSchema = (properties: JsonFormPropertyV2[], requiredList: string[]) => {
  const zodSchemaMap: Record<string, z.ZodTypeAny> = {}

  const filteredRequiredList = requiredList.filter(
    (propertyId) => properties.find((field) => field.id === propertyId)?.required !== false
  )

  const required: string[] = Array.from(
    new Set([...filteredRequiredList, ...properties.filter((field) => field.required).map((field) => field.id)])
  )

  properties.forEach((field) => {
    let fieldSchema

    switch (field.type) {
      case 'string':
        fieldSchema = z.string()
        if (field.format === 'date') {
          fieldSchema = z.union([
            z.date(),
            z.string().regex(/^\d{4}-\d{2}-\d{2}$/, {
              message: 'Please Select a valid date',
            }),
            z.literal(''),
          ])
        } else if (field.format === 'date-time' || field.format === 'datetime') {
          // Enforce canonical format: yyyy-MM-dd'T'HH:mm:ss
          fieldSchema = z.union([
            z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/, {
              message: 'Please Select a valid date and time',
            }),
            z.literal(''),
          ])
        } else if (field.format === 'file-url') {
          fieldSchema = z.instanceof(File)
        } else if (field.format === 'uri') {
          fieldSchema = z.string().url()
        } else {
          if (field.minLength)
            fieldSchema = fieldSchema.min(field.minLength, {
              message: field.validation?.messages?.minLength ?? `Must be at least ${field.minLength} characters`,
            })
          if (field.maxLength)
            fieldSchema = fieldSchema.max(field.maxLength, {
              message: field.validation?.messages?.maxLength ?? `Must be at most ${field.maxLength} characters`,
            })
          if (field.pattern)
            fieldSchema = fieldSchema.regex(new RegExp(field.pattern), {
              message: field.validation?.messages?.pattern ?? `Please enter a valid value for ${field.title}`,
            })
          if (field.choices) {
            if (typeof field.choices[0] === 'string') {
              fieldSchema = z.enum(field.choices as [string, ...string[]]).or(z.literal(''))
            } else {
              fieldSchema = z
                .enum(field.choices.map((option) => (option as { value: string }).value) as [string, ...string[]])
                .or(z.literal(''))
            }
          }
        }
        break

      case 'integer':
      case 'number':
        // Support numeric enums (choices) and allow empty string when UI clears value
        if (field.choices && field.choices.length > 0) {
          const firstChoice = field.choices[0]
          if (typeof firstChoice === 'number') {
            const nanAsEmpty = z.custom((v) => typeof v === 'number' && Number.isNaN(v))
            fieldSchema = buildNumberLiteralUnion(field.choices as number[])
              .or(z.literal(''))
              .or(nanAsEmpty)
              .transform((val) => (typeof val === 'number' && Number.isNaN(val) ? '' : val))
          } else {
            const values = (field.choices as Array<string | number | { value: string | number }>).map((c) =>
              typeof c === 'string' || typeof c === 'number' ? c : getEnumValue(c)
            )
            const numericValues = values.filter((v): v is number => typeof v === 'number')
            const nanAsEmpty = z.custom((v) => typeof v === 'number' && Number.isNaN(v))
            fieldSchema = numericValues.length
              ? buildNumberLiteralUnion(numericValues)
                  .or(z.literal(''))
                  .or(nanAsEmpty)
                  .transform((val) => (typeof val === 'number' && Number.isNaN(val) ? '' : val))
              : z
                  .number()
                  .or(z.literal(''))
                  .or(nanAsEmpty)
                  .transform((val) => (typeof val === 'number' && Number.isNaN(val) ? '' : val))
          }
        } else {
          let numberSchema = z.number()
          if (field.minimum !== undefined) numberSchema = numberSchema.min(field.minimum)
          if (field.maximum !== undefined) numberSchema = numberSchema.max(field.maximum)
          const nanAsEmpty = z.custom((v) => typeof v === 'number' && Number.isNaN(v))
          fieldSchema = numberSchema
            .or(z.literal(''))
            .or(nanAsEmpty)
            .transform((val) => (typeof val === 'number' && Number.isNaN(val) ? '' : val))
        }
        break

      case 'boolean':
        fieldSchema = z.boolean()
        break

      case 'array':
        if (field.items?.choices) {
          const optionType = field.items.choices[0]
          if (typeof optionType === 'string') {
            fieldSchema = z.array(z.enum(field.items.choices as [string, ...string[]]))
          } else if (typeof optionType === 'number') {
            const element = buildNumberLiteralUnion(field.items.choices as number[])
            fieldSchema = z.array(element)
          } else {
            const values = (field.items.choices as Array<string | number | { value: string | number }>).map((c) =>
              typeof c === 'string' || typeof c === 'number' ? c : getEnumValue(c)
            )
            if (values.every((v) => typeof v === 'string')) {
              fieldSchema = z.array(z.enum(values as [string, ...string[]]))
            } else {
              const numericValues = values.filter((v): v is number => typeof v === 'number')
              const element = buildNumberLiteralUnion(numericValues)
              fieldSchema = z.array(element)
            }
          }
        } else if (field.items?.type === 'object' && field.items?.properties) {
          const objectShape: Record<string, z.ZodTypeAny> = {}
          for (const prop of field.items.properties) {
            switch (prop.type) {
              case 'string':
                if (prop.choices) {
                  const choiceValues = (prop.choices as Array<string | number | { value: string | number }>).map((c) =>
                    typeof c === 'string' || typeof c === 'number' ? String(c) : String(getEnumValue(c) ?? '')
                  )
                  objectShape[prop.id] = z
                    .enum(choiceValues as [string, ...string[]])
                    .or(z.literal(''))
                    .optional()
                } else {
                  objectShape[prop.id] = z.string().optional()
                }
                break
              case 'integer':
              case 'number':
                objectShape[prop.id] = z.number().or(z.literal('')).optional()
                break
              case 'boolean':
                objectShape[prop.id] = z.boolean().optional()
                break
              default:
                objectShape[prop.id] = z.any().optional()
            }
          }
          fieldSchema = z.array(z.object(objectShape))
        } else {
          fieldSchema = z.array(z.string())
        }
        break

      case 'cascading':
        {
          // Create a schema for the cascading field
          const cascadingSchema: Record<string, z.ZodTypeAny> = {}

          // Determine present levels using labels or multi-select flags. Always include level1 & level2.
          const presentLevels: Array<'level1' | 'level2' | 'level3' | 'level4' | 'level5'> = ['level1', 'level2']
          if (field.fieldLabels?.level3 !== undefined || field.isMultiSelect?.level3 !== undefined) {
            presentLevels.push('level3')
          }
          if (field.fieldLabels?.level4 !== undefined || field.isMultiSelect?.level4 !== undefined) {
            presentLevels.push('level4')
          }
          if (field.fieldLabels?.level5 !== undefined || field.isMultiSelect?.level5 !== undefined) {
            presentLevels.push('level5')
          }

          const leafLevel = presentLevels[presentLevels.length - 1]

          // Build schema: non-leaf levels are single-select strings; leaf can be string or array
          for (const lvl of presentLevels) {
            const isLeaf = lvl === leafLevel
            const canMulti = (field.isMultiSelect as Record<string, boolean | undefined>)?.[lvl] === true
            if (isLeaf && canMulti) {
              cascadingSchema[lvl] = z.array(z.string()).optional()
            } else {
              cascadingSchema[lvl] = z.string().optional()
            }
          }

          fieldSchema = z.object(cascadingSchema)
        }
        break

      default:
        fieldSchema = z.any()
    }

    const hasDynamicVisibility = field.visibleWhen !== undefined

    if (required?.includes(field.id) && !hasDynamicVisibility) {
      // Enforce that a selection was made for required fields
      if (field.type === 'array') {
        // Arrays must have at least one entry
        try {
          zodSchemaMap[field.id] = (fieldSchema as z.ZodArray<z.ZodTypeAny>).nonempty({
            message: field.items?.choices ? 'Please select at least one option' : 'Please add at least one entry',
          })
        } catch {
          zodSchemaMap[field.id] = fieldSchema
        }
      } else if (field.type === 'cascading') {
        // All cascading levels that exist must be filled
        // Detect present levels consistently with schema building (via labels OR multi-select flags)
        const expectedLevels: Array<'level1' | 'level2' | 'level3' | 'level4' | 'level5'> = ['level1', 'level2']
        if (field.fieldLabels?.level3 !== undefined || field.isMultiSelect?.level3 !== undefined)
          expectedLevels.push('level3')
        if (field.fieldLabels?.level4 !== undefined || field.isMultiSelect?.level4 !== undefined)
          expectedLevels.push('level4')
        if (field.fieldLabels?.level5 !== undefined || field.isMultiSelect?.level5 !== undefined)
          expectedLevels.push('level5')
        zodSchemaMap[field.id] = (fieldSchema as z.ZodObject<z.ZodRawShape>).refine(
          (val) => {
            if (!val || typeof val !== 'object') return false
            return expectedLevels.every((key) => {
              const v = (val as Record<string, unknown>)[key]
              if (Array.isArray(v)) return v.length > 0
              return typeof v === 'string' ? v !== '' : false
            })
          },
          { message: 'Please complete all selections' }
        )
      } else {
        // Disallow empty string sentinel for required scalar fields
        zodSchemaMap[field.id] = fieldSchema.refine((val) => val !== '', {
          message: 'Please select a value',
        })
      }
    } else {
      if (field.type === 'cascading') {
        // Optional cascading: allow any combination of selections or none
        zodSchemaMap[field.id] = (fieldSchema as z.ZodObject<z.ZodRawShape>).optional()
      } else {
        zodSchemaMap[field.id] = fieldSchema.optional()
      }
    }
  })

  const baseObject = z.object(zodSchemaMap)

  // Attach cross-field validation enforcing requireWhen rules and dynamic visibility requirements
  return baseObject.superRefine((data, ctx) => {
    const dataRecord = data as Record<string, unknown>

    for (const field of properties) {
      if (!isFieldVisible(field, dataRecord)) continue

      if (field.visibleWhen && required.includes(field.id)) {
        const value = dataRecord[field.id]
        const isEmpty =
          value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)

        if (isEmpty) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [field.id],
            message: 'Please select a value',
          })
        }
      }

      // Support requireWhen declared on the field itself or nested under validation for backward compatibility
      const requireWhen: RequireWhenRule | undefined = field.requireWhen ?? field.validation?.requireWhen
      if (!requireWhen) continue

      const anyOfResult = evalGroup(data as Record<string, unknown>, requireWhen.anyOf as RequireWhenAnyOf | undefined)
      const allOf = requireWhen.allOf as RequireWhenAnyOf | undefined
      const allOfResult = allOf
        ? Object.entries(allOf).every(([key, cond]) =>
            evalOp(
              (data as Record<string, unknown>)[key],
              (cond as { operator: RequireWhenOperator }).operator,
              (cond as { value: unknown }).value
            )
          )
        : false

      const isTriggered = anyOfResult.matched || allOfResult
      if (!isTriggered) continue

      const value = (data as Record<string, unknown>)[field.id]
      const isEmpty =
        value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)

      if (isEmpty) {
        const messages = field.validation?.messages as ({ requireWhen?: string } & Record<string, unknown>) | undefined
        const message = messages?.requireWhen
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [field.id],
          message: message || 'This field is required based on other selections',
        })
      }
    }
  })
}
